import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Session-level dedup: track which paths have been recorded this session
const trackedPaths = new Set<string>();

/**
 * Hook to track page visits.
 * Deduplicates: only fires once per unique path per browser session.
 */
export const useVisitTracker = () => {
  const location = useLocation();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Clear any pending debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const path = location.pathname;

      // Skip admin routes
      if (path.startsWith('/admin')) return;

      // Skip if already tracked this session
      if (trackedPaths.has(path)) {
        console.debug('[VisitTracker] Already tracked this session:', path);
        return;
      }

      trackedPaths.add(path);

      supabase.functions.invoke('track-visit', {
        body: { page_path: path },
      }).catch((error) => {
        console.debug('Visit tracking failed:', error);
      });
    }, 500); // debounce 500ms to avoid rapid route transitions

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [location.pathname]);
};
